import { useUI } from '../context/UIContext';
import { useMission } from '../context/MissionContext';
import { useCesium } from '../context/CesiumContext';
import { getAllGroundStations, mapApiGSToInternal } from '../service/groundStationApi';
import { getAllGeoFeatures } from '../service/geoFeatureApi';
import { applyOrbitReportUpdatesToTreeItems, mapApiSatToTreeItem } from '../lib/missionSimulationMapper';
import MissionService from '../../../../service/MissionService';
import type { GeoFeature } from '../types/mission';
import type { DrawerMode } from '../types/mdai';
import type { Strategy } from '../types/mission';

export function useMDCockpit() {
  const { state: ui, ...uiActions } = useUI();
  const mission = useMission();
  const cesium = useCesium();

  return {
    // UI state
    aiPanelOpen:         ui.aiPanelOpen,
    drawerMode:          ui.drawerMode as DrawerMode,
    selectedItemId:      ui.selectedItemId,
    selectedItemType:  ui.selectedItemType,
    selectedGeoFeatureIds: ui.selectedGeoFeatureIds,
    orbitalEditTargetId: ui.orbitalEditTargetId,
    groupEditTargetId:   ui.groupEditTargetId,
    layers:              ui.layers,
    addSatModalOpen:     ui.addSatModalOpen,
    addGSModalOpen:      ui.addGSModalOpen,
    editingGSId:         ui.editingGSId,
    geoFeaturePanelOpen: ui.geoFeaturePanelOpen,
    editingFeatureId:    ui.editingFeatureId,
    analyserOpen:        ui.analyserOpen,
    coverageAnalyserOpen: ui.coverageAnalyserOpen,
    sensorConfigOpen:    ui.sensorConfigOpen,
    analyserSourceIds:   ui.analyserSourceIds,
    analyserGfIds:       ui.analyserGfIds,
    analyserGsIds:       ui.analyserGsIds,
    selectedReportId:    ui.selectedReportId,
    selectedReportDetail: ui.selectedReportDetail,
    selectedCoverageReportId: ui.selectedCoverageReportId,
    selectedCoverageReportDetail: ui.selectedCoverageReportDetail,
    allFilters:          ui.allFilters,
    summaryData:         ui.summaryData,
    eventsData:          ui.eventsData,
    coverageSectionTab:  ui.coverageSectionTab ?? 'simple',
    heatmapMetricMode:   ui.heatmapMetricMode ?? 'max',
    revisitThresholds:   ui.revisitThresholds ?? [2, 4, 6, 8],

    // UI actions
    toggleAIPanel:       uiActions.toggleAIPanel,
    setDrawerMode:       uiActions.setDrawerMode,
    selectItem:          uiActions.selectItem,
    selectTreeItem:      uiActions.selectTreeItem,
    toggleSelectedGeoFeature: uiActions.toggleSelectedGeoFeature,
    openSatelliteOrbitEdit: uiActions.openSatelliteOrbitEdit,
    openGroupOrbitEdit:  uiActions.openGroupOrbitEdit,
    toggleLayer:         uiActions.toggleLayer,
    openAddSat:          uiActions.openAddSat,
    closeAddSat:         uiActions.closeAddSat,
    openAddGS:           uiActions.openAddGS,
    closeAddGS:          uiActions.closeAddGS,
    openGeoFeature:      uiActions.openGeoFeature,
    closeGeoFeature:     uiActions.closeGeoFeature,
    openEditGeoFeature:  uiActions.openEditGeoFeature,
    openAnalyser:        uiActions.openAnalyser,
    closeAnalyser:       uiActions.closeAnalyser,
    openCoverageAnalyser: uiActions.openCoverageAnalyser,
    closeCoverageAnalyser: uiActions.closeCoverageAnalyser,
    openSensorConfig:     uiActions.openSensorConfig,
    closeSensorConfig:    uiActions.closeSensorConfig,
    leftPanelCollapsed:  ui.leftPanelCollapsed,
    setLeftPanelCollapsed: uiActions.setLeftPanelCollapsed,
    modelConfigOpen:     ui.modelConfigOpen,
    openModelConfig:     uiActions.openModelConfig,
    closeModelConfig:    uiActions.closeModelConfig,
    viewMode:            ui.viewMode,
    setViewMode:         uiActions.setViewMode,
    modelData:           ui.modelData,
    reportHoverHighlights: ui.reportHoverHighlights,
    setModelData:        uiActions.setModelData,
    timelineCollapsed:   ui.timelineCollapsed,
    timelineHeight:      ui.timelineHeight,
    setTimelineCollapsed: uiActions.setTimelineCollapsed,
    setTimelineHeight:   uiActions.setTimelineHeight,
    setReportHoverHighlights: uiActions.setReportHoverHighlights,
    shouldBlockUI:       ui.shouldBlockUI,
    setShouldBlockUI:    uiActions.setShouldBlockUI,
    toggleAnalyserSource: uiActions.toggleAnalyserSource,
    toggleAnalyserGf:    uiActions.toggleAnalyserGf,
    toggleAnalyserGs:    uiActions.toggleAnalyserGs,
    clearAnalyserSelection: uiActions.clearAnalyserSelection,
    setAnalyserSelection: uiActions.setAnalyserSelection,
    setSelectedReportId: uiActions.setSelectedReportId,
    setSelectedReportDetail: uiActions.setSelectedReportDetail,
    setSelectedCoverageReportId: uiActions.setSelectedCoverageReportId,
    setSelectedCoverageReportDetail: uiActions.setSelectedCoverageReportDetail,
    selectedCoverageSensors: ui.selectedCoverageSensors,
    setSelectedCoverageSensors: uiActions.setSelectedCoverageSensors,
    setAllFilters:       uiActions.setAllFilters,
    setSummaryData:      uiActions.setSummaryData,
    setEventsData:       uiActions.setEventsData,
    setCoverageSectionTab: uiActions.setCoverageSectionTab,
    setHeatmapMetricMode: uiActions.setHeatmapMetricMode,
    setRevisitThresholds: uiActions.setRevisitThresholds,
    accessWindowStatus:  ui.accessWindowStatus,
    setAccessWindowStatus: uiActions.setAccessWindowStatus,
    invalidateAccessWindows: () => {
      uiActions.setAccessWindowStatus(null);
      mission.setAccessWindowPairs([]);
    },
    timelineResetTrigger: ui.timelineResetTrigger ?? 0,
    triggerTimelineReset: uiActions.triggerTimelineReset,

    // Mission data
    satellites:       mission.state.satellites,
    geoFeatures:      mission.state.geoFeatures,
    groundStations:   mission.state.groundStations,
    accessWindowPairs: mission.state.accessWindowPairs,
    accessAnalysisReports: mission.state.accessAnalysisReports,
    coverageAnalysisReports: mission.state.coverageAnalysisReports,
    params:           mission.state.params,
    currentStrategy:  mission.state.currentStrategy,

    // Mission mutations
    setGeoFeatures:       mission.setGeoFeatures,
    setStrategy:          (letter: 'A' | 'B' | 'C') => mission.setStrategy(letter),
    updateParams:         (partial: Partial<Strategy>) => mission.updateParams(partial),
    toggleSatActive:      mission.toggleSatActive,
    toggleGSActive:       mission.toggleGSActive,
    toggleSatGroupExpand: mission.toggleSatGroupExpand,
    addGeoFeature:        mission.addGeoFeature,
    updateGeoFeature:     mission.updateGeoFeature,
    toggleGeoFeatureActive: mission.toggleGeoFeatureActive,
    removeGeoFeature:     mission.removeGeoFeature,
    setSatellites:        mission.setSatellites,
    addSatellite:         mission.addSatellite,
    updateSatellite:      mission.updateSatellite,
    updateSatellites:     mission.updateSatellites,
    applyOrbitEvent:      mission.applyOrbitEvent,
    resetOrbitData:       mission.resetOrbitData,
    setGroundStations:    mission.setGroundStations,
    addGroundStation:     mission.addGroundStation,
    updateGroundStation:  mission.updateGroundStation,
    setAccessWindowPairs: mission.setAccessWindowPairs,
    setAccessAnalysisReports: mission.setAccessAnalysisReports,
    upsertAccessAnalysisReport: mission.upsertAccessAnalysisReport,
    setCoverageAnalysisReports: mission.setCoverageAnalysisReports,
    upsertCoverageAnalysisReport: mission.upsertCoverageAnalysisReport,

    fetchGroundStations: async (missionId: string, scenarioId: string) => {
      try {
        const res = await getAllGroundStations(missionId, scenarioId);
        const list = (res?.data?.data ?? []).map(mapApiGSToInternal);
        mission.setGroundStations(list);
        cesium.addGroundStations(list);
      } catch (e) {
        console.error('[GS] fetchGroundStations failed', e);
      }
    },

    fetchGeoFeatures: async (missionId: string, scenarioId: string) => {
      try {
        const res = await getAllGeoFeatures(missionId, scenarioId);
        const raw: any[] = res?.data?.data ?? res?.data ?? [];
        const list: GeoFeature[] = raw.map((aoi: any) => {
          const hasPolygon = Array.isArray(aoi.polygon) && aoi.polygon.length > 0;
          if (hasPolygon) {
            const rawCoords = aoi.polygon.slice(0, -1).map((p: number[]) => ({ lat: p[1], lng: p[0] }));
            const uniqueCoords: { lat: number; lng: number }[] = [];
            for (const pt of rawCoords) {
              if (pt == null || isNaN(pt.lat) || isNaN(pt.lng)) continue;
              if (uniqueCoords.length === 0) {
                uniqueCoords.push(pt);
              } else {
                const prev = uniqueCoords[uniqueCoords.length - 1];
                if (Math.abs(pt.lat - prev.lat) > 1e-7 || Math.abs(pt.lng - prev.lng) > 1e-7) {
                  uniqueCoords.push(pt);
                }
              }
            }
            if (uniqueCoords.length > 1) {
              const first = uniqueCoords[0];
              const last = uniqueCoords[uniqueCoords.length - 1];
              if (Math.abs(first.lat - last.lat) < 1e-7 && Math.abs(first.lng - last.lng) < 1e-7) {
                uniqueCoords.pop();
              }
            }
            return {
              id: aoi.areaOfInterestId ?? `aoi-${Date.now()}`,
              name: aoi.name,
              kind: 'polygon' as const,
              type: aoi.type,
              active: true,
              coordinates: uniqueCoords,
              createdAt: aoi.createdAt || aoi.created_at || aoi.timestamp || undefined,
            };
          }
          const isCartesian = aoi.coordinateType?.toLowerCase() === 'cartesian';
          return {
            id: aoi.areaOfInterestId ?? `aoi-${Date.now()}`,
            name: aoi.name,
            kind: 'point' as const,
            type: aoi.type,
            active: true,
            coordType: aoi.coordinateType?.toLowerCase() as any,
            altDatum: aoi.altitudeReference as any,
            altUnit: (aoi.unit ?? 'km') as any,
            createdAt: aoi.createdAt || aoi.created_at || aoi.timestamp || undefined,
            ...(isCartesian ? {
              x: aoi.cartesianCoordinates?.[0],
              y: aoi.cartesianCoordinates?.[1],
              z: aoi.cartesianCoordinates?.[2],
            } : {
              lat: aoi.coordinates?.lat ?? 0,
              lng: aoi.coordinates?.long ?? 0,
              altitude: aoi.coordinates?.altitude ?? 0,
              minElevationAngle: aoi.coordinates?.minElevationAngle ?? 0,
            }),
          };
        });
        mission.setGeoFeatures(list);
        list.forEach(gf => cesium.commitGeoFeature(gf));
      } catch (e) {
        console.error('[GeoFeature] fetchGeoFeatures failed', e);
      }
    },

    fetchSatellites: async (missionId: string, scenarioId: string) => {
      try {
        const satRes = await MissionService.getSatelliteGroups(missionId, scenarioId);
        const list: any[] = satRes?.data?.data ?? satRes?.data ?? [];

        if (!Array.isArray(list) || list.length === 0) {
          mission.setSatellites([]);
          return;
        }

        const orbitRes = await MissionService.getAllSatelliteGroupOrbit(missionId, scenarioId)
          .catch((error: unknown) => {
            console.error('[OrbitPoints] BE orbit readiness check failed', error);
            return null;
          });
        const reports: any[] = orbitRes?.data?.data ?? orbitRes?.data ?? [];
        const mapped = list.map(mapApiSatToTreeItem);
        const resolved = Array.isArray(reports) && reports.length > 0
          ? applyOrbitReportUpdatesToTreeItems(mapped, reports)
          : mapped;
        mission.setSatellites(resolved);
      } catch (error) {
        console.error('[SatelliteGroups] fetchSatellites failed', error);
        mission.setSatellites([]);
      }
    },
  };
}
